// twoshot picks (§5.4). Three modes the pair generates independently. First
// visit shows a "Generate" button per mode (cost is intentional); results cache
// server-side and show a "regenerate" affordance.

import { useEffect, useState } from "react";
import { generateRecs } from "../lib/api.ts";

export interface Pick {
  title: string;
  year?: number;
  blurb: string;
}

const MODES: { key: string; label: string; hint: string; desc: string; cta: string }[] = [
  {
    key: "newground",
    label: "New ground",
    desc: "Films neither of you has seen — fresh common ground for you both.",
    hint: "Films neither of you has seen — grounded in what you both love.",
    cta: "Generate new-ground picks",
  },
  {
    key: "bridge",
    label: "Bridge picks",
    desc: "One of you loves it, the other hasn't seen it — a nudge to close the gap.",
    hint: "Films one of you loves that the other hasn't seen — with the case for why they'll click.",
    cta: "Generate bridge picks",
  },
  {
    key: "referee",
    label: "Tonight's referee",
    desc: "Your shared watchlist, ranked — settle what to actually put on.",
    hint: "Your mutual watchlist, ranked, with a one-line case for each.",
    cta: "Rank our watchlist",
  },
];

export interface SeenSets {
  /** Titles either user has watched (union). */
  either: Set<string>;
  /** Titles both users have watched (intersection). */
  both: Set<string>;
}

export function PicksPanel({
  blendSlug,
  recs,
  seen,
  readOnly = false,
}: {
  blendSlug: string;
  recs: Record<string, unknown> | null;
  /** Watched-title sets used to filter picks against what's already been seen. */
  seen?: SeenSets;
  /** Public (non-member) view: show cached picks but no generate/regenerate. */
  readOnly?: boolean;
}) {
  const [active, setActive] = useState<string>("newground");
  return (
    <div data-testid="picks-panel" className="rounded-xl border border-red-600/30 bg-card p-4">
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`action-picks-tab-${m.key}`}
            className={`rounded-lg border p-3 text-left transition-colors ${
              active === m.key ? "border-red-600 bg-red-600/5" : "border-border-soft bg-paper-50 hover:border-border-strong"
            }`}
            onClick={() => setActive(m.key)}
          >
            <div className={`text-sm font-semibold ${active === m.key ? "text-red-600" : "text-ink-900"}`}>{m.label}</div>
            <div className="mt-0.5 text-xs leading-snug text-ink-500">{m.desc}</div>
          </button>
        ))}
      </div>
      {MODES.filter((m) => m.key === active).map((m) => (
        <PicksMode
          key={m.key}
          blendSlug={blendSlug}
          mode={m}
          readOnly={readOnly}
          initial={recs?.[m.key] as Pick[] | undefined}
          seen={seen}
        />
      ))}
    </div>
  );
}

const norm = (t: string, y?: number) => `${t.trim().toLowerCase()}|${y ?? ""}`;

/**
 * Clean and filter model output (§12):
 * - every pick must have a title AND a non-empty blurb (drop malformed ones)
 * - new ground: drop anything EITHER user has watched (neither should have seen it)
 * - bridge / referee: drop anything BOTH have already watched (no point suggesting
 *   a film they've both seen)
 * - cap at 5
 */
export function filterPicks(mode: string, picks: Pick[], seen?: SeenSets): Pick[] {
  const valid = (picks ?? []).filter(
    (p) => p && typeof p.title === "string" && p.title.trim() && typeof p.blurb === "string" && p.blurb.trim(),
  );
  if (!seen) return valid.slice(0, 5);
  const drop = mode === "newground" ? seen.either : seen.both;
  return valid.filter((p) => !drop.has(norm(p.title, p.year))).slice(0, 5);
}

function PicksMode({
  blendSlug,
  mode,
  initial,
  seen,
  readOnly,
}: {
  blendSlug: string;
  mode: { key: string; label: string; hint: string; cta: string };
  initial?: Pick[];
  seen?: SeenSets;
  readOnly?: boolean;
}) {
  const [picks, setPicks] = useState<Pick[] | null>(
    initial ? filterPicks(mode.key, initial, seen) : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // force=true is the Regenerate action: it bypasses the 24h cache so a fresh
  // set is produced (otherwise the server returns the identical cached list and
  // the button appears to do nothing).
  async function generate(force = false) {
    setLoading(true);
    setError(null);
    try {
      const res = (await generateRecs(blendSlug, mode.key, force)) as { picks?: Pick[] } | Pick[];
      const list = Array.isArray(res) ? res : res.picks ?? [];
      setPicks(filterPicks(mode.key, list, seen));
    } catch (err) {
      const msg = (err as Error).message || "";
      setError(msg.includes("rate") ? "Already generated today — try again tomorrow." : "Couldn't generate picks, try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid={`picks-${mode.key}`}>
      <p className="mb-3 text-sm text-ink-400">{mode.hint}</p>
      {loading ? (
        <PicksGenerating mode={mode.key} regenerating={!!picks} />
      ) : picks ? (
        <>
          {/* keyed by mode so the rows re-print (animate in) when you switch modes */}
          <ul key={mode.key} className="reprint space-y-3">
            {picks.map((p, i) => (
              <li key={i} className="rounded-lg bg-paper-100 p-3" data-testid="pick-item">
                <p className="font-medium text-ink-900">
                  {p.title} {p.year ? <span className="text-ink-400">({p.year})</span> : null}
                </p>
                <p className="mt-1 text-sm text-ink-400">{p.blurb}</p>
              </li>
            ))}
          </ul>
          {!picks.length && (
            <p className="rounded-lg border border-dashed border-border-strong p-4 text-sm text-ink-400">
              Nothing new to suggest here — you two have seen it all.
            </p>
          )}
          {!readOnly && (
            <button type="button" data-testid={`action-regenerate-${mode.key}`} className="mt-3 text-sm text-ink-400 underline" onClick={() => generate(true)}>
              Regenerate
            </button>
          )}
        </>
      ) : readOnly ? (
        <p className="rounded-lg border border-dashed border-border-strong p-4 text-sm text-ink-400">
          Not generated yet — only the two of them can create these picks.
        </p>
      ) : (
        <button type="button" data-testid={`action-generate-${mode.key}`} className="btn-primary" onClick={() => generate()}>
          {mode.cta}
        </button>
      )}
      {error && <p data-testid="picks-error" className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// Generation loading state — shimmering pick cards + a rotating status line that
// steps through the model's actual stages. Animations freeze in test mode.
const STATUS: Record<string, string[]> = {
  newground: ["Reading both your tastes…", "Finding common ground…", "Writing the case for each…"],
  bridge: ["Reading their taste…", "Finding what they've missed…", "Making the argument…"],
  referee: ["Weighing your watchlist…", "Ranking the contenders…", "Writing each verdict…"],
};

export function PicksGenerating({ mode, regenerating }: { mode: string; regenerating: boolean }) {
  const [step, setStep] = useState(0);
  const steps = STATUS[mode] ?? STATUS.newground;
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % steps.length), 2200);
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <div data-testid={`picks-generating-${mode}`}>
      <div className="mb-3 flex items-center gap-2 text-sm text-ink-900">
        <span className="inline-flex gap-0.5" aria-hidden>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" style={{ animationDelay: "0ms" }} />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" style={{ animationDelay: "150ms" }} />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" style={{ animationDelay: "300ms" }} />
        </span>
        {regenerating ? "Regenerating" : steps[step]}
      </div>
      <ul className="space-y-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <li key={i} className="rounded-lg bg-paper-100 p-3">
            <div className="h-4 w-2/5 rounded shimmer" />
            <div className="mt-2 h-3 w-11/12 rounded shimmer" style={{ opacity: 0.7 }} />
            <div className="mt-1.5 h-3 w-3/5 rounded shimmer" style={{ opacity: 0.5 }} />
          </li>
        ))}
      </ul>
    </div>
  );
}
